import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { PageLayout } from "@/components/PageLayout";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

const LogicSimplifierCalculator = () => {
  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Math", url: "https://calclab.app/math" },
      { name: "Logic Simplifier", url: "https://calclab.app/calculator/logic-simplifier" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Logic Simplifier",
      "Simplify Boolean expressions using algebraic rules. Free online logic calculator.",
      "https://calclab.app/calculator/logic-simplifier",
      lang
    )
  ]);
  const [expression, setExpression] = useState("");
  const [simplified, setSimplified] = useState("");
  const [steps, setSteps] = useState<string[]>([]);

  const simplifyExpression = () => {
    let current = expression.toUpperCase();
    const stepsList: string[] = [current];

    // Apply basic boolean algebra rules
    // Double negation: ¬¬A = A
    current = current.replace(/NOT\s*NOT\s*(\w)/g, "$1");
    if (current !== stepsList[stepsList.length - 1]) stepsList.push(current);

    // Identity laws: A AND TRUE = A, A OR FALSE = A
    current = current.replace(/(\w+)\s*AND\s*TRUE/gi, "$1");
    current = current.replace(/(\w+)\s*OR\s*FALSE/gi, "$1");
    if (current !== stepsList[stepsList.length - 1]) stepsList.push(current);

    // Domination laws: A AND FALSE = FALSE, A OR TRUE = TRUE
    current = current.replace(/(\w+)\s*AND\s*FALSE/gi, "FALSE");
    current = current.replace(/(\w+)\s*OR\s*TRUE/gi, "TRUE");
    if (current !== stepsList[stepsList.length - 1]) stepsList.push(current);

    // Idempotent laws: A AND A = A, A OR A = A
    current = current.replace(/(\w+)\s*AND\s*\1/gi, "$1");
    current = current.replace(/(\w+)\s*OR\s*\1/gi, "$1");
    if (current !== stepsList[stepsList.length - 1]) stepsList.push(current);

    // Complement laws: A AND NOT A = FALSE, A OR NOT A = TRUE
    current = current.replace(/(\w+)\s*AND\s*NOT\s*\1/gi, "FALSE");
    current = current.replace(/(\w+)\s*OR\s*NOT\s*\1/gi, "TRUE");
    if (current !== stepsList[stepsList.length - 1]) stepsList.push(current);

    // De Morgan's laws: NOT(A AND B) = NOT A OR NOT B
    current = current.replace(/NOT\s*\(([^)]+)\s*AND\s*([^)]+)\)/gi, "(NOT $1 OR NOT $2)");
    if (current !== stepsList[stepsList.length - 1]) stepsList.push(current);

    // De Morgan's laws: NOT(A OR B) = NOT A AND NOT B
    current = current.replace(/NOT\s*\(([^)]+)\s*OR\s*([^)]+)\)/gi, "(NOT $1 AND NOT $2)");
    if (current !== stepsList[stepsList.length - 1]) stepsList.push(current);

    setSimplified(current);
    setSteps(stepsList);
  };

  return (
    <PageLayout>
      <div className="max-w-2xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Logic Simplifier</CardTitle>
            <CardDescription>Simplify Boolean expressions using algebraic rules</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="expression">Boolean Expression</Label>
              <Input
                id="expression"
                placeholder="A AND NOT A"
                value={expression}
                onChange={(e) => setExpression(e.target.value)}
              />
              <p className="text-sm text-muted-foreground">
                Use: AND, OR, NOT with capital letters A-Z as variables
              </p>
            </div>

            <Button onClick={simplifyExpression} className="w-full">
              Simplify Expression
            </Button>
            </CardContent>
        </Card>

        {simplified && (
          <>
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-2">Simplified Expression</h3>
              <p className="text-2xl font-mono p-4 bg-muted rounded">{simplified}</p>
            </Card>

            {steps.length > 1 && (
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Simplification Steps</h3>
                <ol className="space-y-2">
                  {steps.map((step, index) => (
                    <li key={index} className="font-mono text-sm">
                      <span className="text-muted-foreground mr-2">{index + 1}.</span>
                      {step}
                    </li>
                  ))}
                </ol>
              </Card>
            )}

            <CalculatorSuperCalcCTA config={calculatorCTAs["logic-simplifier"]} trigger={simplified} />
          </>
        )}

        <Card className="p-6 bg-muted/50">
          <h3 className="font-semibold mb-2">Boolean Algebra Rules Applied</h3>
          <ul className="space-y-1 text-sm text-muted-foreground">
            <li>• <strong>Double Negation:</strong> NOT NOT A = A</li>
            <li>• <strong>Identity Laws:</strong> A AND TRUE = A, A OR FALSE = A</li>
            <li>• <strong>Domination Laws:</strong> A AND FALSE = FALSE, A OR TRUE = TRUE</li>
            <li>• <strong>Idempotent Laws:</strong> A AND A = A, A OR A = A</li>
            <li>• <strong>Complement Laws:</strong> A AND NOT A = FALSE, A OR NOT A = TRUE</li>
            <li>• <strong>De Morgan's Laws:</strong> NOT(A AND B) = NOT A OR NOT B</li>
          </ul>
        </Card>
      </div>
    </PageLayout>
  );
};

export default LogicSimplifierCalculator;
